import { DEFAULT_SETTINGS, ReliableStream, type Settings, type StreamHeader } from './reliableStream'

/**
 * The browser's end of one stream of the reliable transport
 * (docs/GatewayReliableTransport.md), with its timer: a terminal session, or
 * a realization run the browser watches. The backend relays its frames; the
 * gateway is the other end.
 *
 * Messages arrive once and in order, and are acknowledged as they're handed
 * on. What this end sends (a terminal's keystrokes, as base64) waits here in
 * order while the stream's bound is full, rather than being dropped.
 */

/** A frame for the backend to relay to the gateway: data as base64, or none. */
export interface OutgoingFrame {
  type: 'stream_frame'
  sessionId: string
  header: StreamHeader
  data?: string
}

export interface StreamEndOptions<In> {
  sessionId: string
  /** Put a frame on the websocket. */
  send: (frame: OutgoingFrame) => void
  /** The gateway's next message, in its order. */
  onMessage: (message: In) => void
  /** The stream gave up or the gateway restarted: the conversation is gone. */
  onLost: () => void
  /** A message's size in bytes, either way, for the stream's bounds. */
  costOf: (message: string | In) => number
  now?: () => number
  settings?: Settings
}

export class StreamEnd<In> {
  private readonly stream: ReliableStream<string, In>
  private readonly queued: string[] = []
  private timer: ReturnType<typeof setTimeout> | undefined
  private lost = false
  private disposed = false
  private readonly now: () => number

  constructor(private readonly opts: StreamEndOptions<In>) {
    this.now = opts.now ?? (() => performance.now())
    this.stream = new ReliableStream<string, In>(opts.sessionId, opts.settings ?? DEFAULT_SETTINGS, opts.costOf)
  }

  /** Data for the gateway, as base64. */
  input(data: string): void {
    if (this.disposed) return
    this.queued.push(data)
    this.pump()
  }

  /** A frame the backend relayed from the gateway. */
  receive(header: StreamHeader, message: In | null | undefined): void {
    if (this.disposed) return
    const now = this.now()
    this.stream.receive(header, message ?? undefined, now)
    for (let got = this.stream.pollReceived(now); got; got = this.stream.pollReceived(now)) {
      if (got.kind === 'closed') {
        // The gateway is done; close this side so the stream finishes.
        this.stream.close(now)
        continue
      }
      this.opts.onMessage(got.message)
    }
    this.pump()
  }

  dispose(): void {
    this.disposed = true
    if (this.timer !== undefined) clearTimeout(this.timer)
    this.timer = undefined
  }

  private pump(): void {
    const now = this.now()
    while (this.queued.length > 0 && this.stream.hasRoom()) {
      if (this.stream.send(this.queued[0], now) !== 'ok') break
      this.queued.shift()
    }
    for (let f = this.stream.pollTransmit(now); f; f = this.stream.pollTransmit(now)) {
      this.opts.send({
        type: 'stream_frame',
        sessionId: this.opts.sessionId,
        header: f.header,
        ...(f.message === undefined ? {} : { data: f.message }),
      })
    }
    const ended = this.stream.ended()
    if ((ended === 'gaveUp' || ended === 'reset') && !this.lost) {
      this.lost = true
      this.dispose()
      this.opts.onLost()
      return
    }
    this.schedule()
  }

  private schedule(): void {
    if (this.timer !== undefined) clearTimeout(this.timer)
    this.timer = undefined
    const at = this.stream.pollTimeout()
    if (at === undefined || this.disposed) return
    this.timer = setTimeout(
      () => {
        this.timer = undefined
        this.stream.handleTimeout(this.now())
        this.pump()
      },
      Math.max(0, at - this.now()),
    )
  }
}
